import styled, { useTheme } from 'styled-components';
import Modal from '../../../components/Modal';
import {
  BodyMedium,
  Heading5Text,
  TitleSmall,
} from '../../../components/Texts';
import { formatUSDEnsure2Digits } from '../../../common/currency';
import Button from '../../../components/Button';
import { useDispatch, useSelector } from 'react-redux';
import { selectTokenByNetworkAndSymbol } from '../../../redux/slices/tokenSlice';
import { useMemo } from 'react';
import { openModal } from '../../../redux/slices/modals';
import { hapticsImpactLight } from '../../../common/haptic';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';

const PaymentOptionContainer = styled.div`
  background-color: ${({ theme }) => theme.background.secondary};
  border-radius: 16px;
  padding: 16px;
  gap: 12px;
  display: flex;
  align-items: center;
  width: 100%;
  transition: all 0.2s ease;

  ${({ disabled, theme }) =>
    !disabled &&
    `
      cursor: pointer;
      @media (hover: hover) {
        &:hover {
          background-color: ${theme.background.secondPrimary};
        }
      }
  `}
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const TokenLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;

const AmountInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const TokenScroll = styled.div`
  max-height: calc(100vh - 385px);
  width: 100%;
  overflow-y: auto;
`;

const PaymentSuggestionModal = ({
  onClose,
  amount,
  targetToken,
  onPaymentMethodSelected,
  tokenOptions,
  disabledTokenOptions = [],
  onCancel,
}) => {
  const dispatch = useDispatch();

  const paymentOptions = useMemo(() => {
    return tokenOptions.map((token) => ({
      token,
      balance: token.balance,
      balanceUSD: token.usdValue,
    }));
  }, [tokenOptions, targetToken]);

  const disabledPaymentOptions = useMemo(() => {
    return disabledTokenOptions.map((token) => ({
      token,
      balance: token.balance,
      balanceUSD: token.usdValue,
    }));
  }, [disabledTokenOptions, targetToken]);

  if (paymentOptions.length === 0) {
    dispatch(openModal({ name: 'addFunds', props: { amount: amount } }));
    return;
  }

  const handlePaymentSelect = async (option) => {
    hapticsImpactLight();
    onPaymentMethodSelected({
      token: option.token,
    });
  };

  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const theme = useTheme();

  return (
    <Modal onClose={onClose} width={!isMobile ? '600px' : '100%'}>
      <div
        style={{
          display: 'flex',
          gap: 24,
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Heading5Text
            style={{
              fontWeight: '600',
              marginTop: 20,
              color: theme.text.title,
            }}
          >
            Payment Method
          </Heading5Text>
        </div>
        <TokenScroll>
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexDirection: 'column',
              width: '100%',
            }}
          >
            {paymentOptions.map((option, index) => (
              <PaymentOptionContainer
                key={index}
                onClick={() => handlePaymentSelect(option)}
              >
                <TokenLogo
                  src={option.token.logoURI}
                  alt={option.token.symbol}
                />
                <TokenInfo>
                  <TitleSmall>
                    {option.token.name}{' '}
                    <span
                      style={{
                        color: `${theme.text.secondary}`,
                        fontWeight: 300,
                        fontSize: 12,
                      }}
                    >
                      ({option.token.symbol})
                    </span>
                  </TitleSmall>
                  <BodyMedium
                    style={{
                      color: `${theme.text.secondary}`,
                      fontSize: 14,
                    }}
                  >
                    {option.description}
                  </BodyMedium>
                </TokenInfo>
                <AmountInfo>
                  <BodyMedium style={{ fontWeight: 600 }}>
                    {formatUSDEnsure2Digits(option.balanceUSD)}
                  </BodyMedium>
                  <BodyMedium
                    style={{
                      color: `${theme.text.secondary}`,
                      fontSize: 12,
                    }}
                  >
                    Available
                  </BodyMedium>
                </AmountInfo>
              </PaymentOptionContainer>
            ))}
            {disabledPaymentOptions.map((option, index) => (
              <PaymentOptionContainer key={index} disabled>
                <TokenLogo
                  src={option.token.logoURI}
                  alt={option.token.symbol}
                  style={{ opacity: 0.5 }}
                />
                <TokenInfo>
                  <TitleSmall style={{ opacity: 0.5 }}>
                    {option.token.name}{' '}
                    <span
                      style={{
                        color: `${theme.text.secondary}`,
                        fontWeight: 300,
                        fontSize: 12,
                      }}
                    >
                      ({option.token.symbol})
                    </span>
                  </TitleSmall>
                  <BodyMedium
                    style={{
                      color: `${theme.text.secondary}`,
                      fontSize: 14,
                    }}
                  >
                    {option.description}
                  </BodyMedium>
                </TokenInfo>
                <AmountInfo>
                  <BodyMedium style={{ fontWeight: 600, opacity: 0.5 }}>
                    {formatUSDEnsure2Digits(option.balanceUSD)}
                  </BodyMedium>
                  <BodyMedium
                    style={{
                      color: `${theme.text.secondary}`,
                      fontSize: 12,
                    }}
                  >
                    Not enough balance
                  </BodyMedium>
                </AmountInfo>
              </PaymentOptionContainer>
            ))}
          </div>
        </TokenScroll>
        <div style={{ marginLeft: 'auto' }}>
          <Button
            variant='white'
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                onClose();
              }
            }}
          >
            CANCEL
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentSuggestionModal;
